import { PageHeader } from '@ant-design/pro-components';
import { Button, Divider, message, Modal, Spin, Switch, Table, Tabs, Tag } from 'antd';
import { Content } from 'antd/es/layout/layout';
import { ColumnsType } from 'antd/es/table';
import React from 'react';
import { useAsyncFn } from 'react-use';
import { PlanActionTypeResponseModel } from '@uniquegood/realworld-admin-interface';
import DefaultLayout from '@src/components/DefaultLayout';
import { planActionApi } from '@src/apis/admin';
import {
  ActionType,
  ACTION_TYPE_LABELS,
  ACTION_TYPE_CATEGORIES,
  ACTION_TYPES,
  getFullActionTypeName,
  ActionCategory,
} from '@src/constants/planAction';

const PLAN_TYPE_LABELS: Record<string, string> = {
  Free: '무료',
  Pro: '프로',
  Enterprise: '엔터프라이즈',
  Class: '클래스',
  Teacher: '선생님',
  Realworld: '리얼월드',
};

interface ActionTypeRow {
  key: ActionType;
  label: string;
  category: ActionCategory;
  isAllowed: boolean;
  isChanged: boolean;
}

export default function PlanAction() {
  const [planActionsState, fetchPlanActions] = useAsyncFn(async () => {
    const { data } = await planActionApi.getPlanActions();
    return data.data;
  }, []);

  const [editedActions, setEditedActions] = React.useState<Record<string, Set<string>>>({});
  const [activeTab, setActiveTab] = React.useState<string>('');
  const [saving, setSaving] = React.useState(false);
  const [confirmModal, setConfirmModal] = React.useState<{ visible: boolean; planId: string | null }>({
    visible: false,
    planId: null,
  });
  const [countdown, setCountdown] = React.useState(3);

  React.useEffect(() => {
    fetchPlanActions();
  }, [fetchPlanActions]);

  React.useEffect(() => {
    if (planActionsState.value && planActionsState.value.length > 0) {
      const initial: Record<string, Set<string>> = {};
      planActionsState.value.forEach((plan: PlanActionTypeResponseModel) => {
        initial[plan.planId] = new Set(plan.actionTypes || []);
      });
      setEditedActions(initial);
      if (!activeTab) {
        setActiveTab(planActionsState.value[0].planId);
      }
    }
  }, [planActionsState.value, activeTab]);

  React.useEffect(() => {
    if (confirmModal.visible) {
      setCountdown(3);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [confirmModal.visible]);

  const openConfirmModal = (planId: string) => {
    setConfirmModal({ visible: true, planId });
  };

  const closeConfirmModal = () => {
    setConfirmModal({ visible: false, planId: null });
  };

  const handleConfirmSave = async () => {
    if (confirmModal.planId) {
      await handleSave(confirmModal.planId);
    }
    closeConfirmModal();
  };

  const handleToggle = (planId: string, actionType: ActionType, checked: boolean) => {
    setEditedActions((prev) => {
      const newSet = new Set(prev[planId]);
      const fullTypeName = getFullActionTypeName(actionType);
      if (checked) {
        newSet.add(fullTypeName);
      } else {
        newSet.delete(fullTypeName);
      }
      return { ...prev, [planId]: newSet };
    });
  };

  const handleSave = async (planId: string) => {
    setSaving(true);
    try {
      const actionTypes = Array.from(editedActions[planId] || []);
      await planActionApi.updatePlanAction(planId, undefined, { actionTypes });
      message.success('저장되었습니다.');
      fetchPlanActions();
    } catch {
      message.error('저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const getDataSource = (planId: string): ActionTypeRow[] => {
    const allowedSet = editedActions[planId] || new Set();
    const original = planActionsState.value?.find((p: PlanActionTypeResponseModel) => p.planId === planId);
    const originalSet = new Set(original?.actionTypes || []);
    return ACTION_TYPES.map((actionType) => {
      const fullTypeName = getFullActionTypeName(actionType);
      const isAllowed = allowedSet.has(fullTypeName);
      const wasAllowed = originalSet.has(fullTypeName);
      return {
        key: actionType,
        label: ACTION_TYPE_LABELS[actionType],
        category: ACTION_TYPE_CATEGORIES[actionType],
        isAllowed,
        isChanged: isAllowed !== wasAllowed,
      };
    });
  };

  const ChangedIndicator = () => (
    <span
      style={{
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: '#1890ff',
      }}
    />
  );

  const getCategoryColor = (category: ActionCategory): string => {
    switch (category) {
      case '기본':
        return 'green';
      case '고급':
        return 'blue';
      case '추가':
        return 'purple';
      default:
        return 'default';
    }
  };

  const getColumns = (planId: string): ColumnsType<ActionTypeRow> => [
    {
      title: '',
      key: 'changed',
      width: 24,
      render: (_: unknown, record: ActionTypeRow) => (
        <div style={{ width: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {record.isChanged && <ChangedIndicator />}
        </div>
      ),
    },
    {
      title: '표시명',
      dataIndex: 'label',
      key: 'label',
      render: (value: string, record: ActionTypeRow) => (
        <span style={{ color: record.isAllowed ? 'inherit' : '#bfbfbf' }}>
          {value}
        </span>
      ),
    },
    {
      title: '분류',
      dataIndex: 'category',
      key: 'category',
      width: 70,
      align: 'center',
      render: (value: ActionCategory, record: ActionTypeRow) => (
        <Tag color={record.isAllowed ? getCategoryColor(value) : 'default'}>
          {value}
        </Tag>
      ),
    },
    {
      title: '허용',
      key: 'isAllowed',
      width: 90,
      align: 'center',
      render: (_: unknown, record: ActionTypeRow) => (
        <Switch
          checked={record.isAllowed}
          onChange={(checked) => handleToggle(planId, record.key, checked)}
          checkedChildren="허용"
          unCheckedChildren="미허용"
          size="small"
        />
      ),
    },
  ];

  const hasChanges = (planId: string): boolean => {
    const original = planActionsState.value?.find((p: PlanActionTypeResponseModel) => p.planId === planId);
    if (!original) return false;
    const originalSet = new Set(original.actionTypes || []);
    const currentSet = editedActions[planId] || new Set();
    if (originalSet.size !== currentSet.size) return true;
    for (const item of originalSet) {
      if (!currentSet.has(item)) return true;
    }
    return false;
  };

  const tabItems = planActionsState.value?.map((planAction: PlanActionTypeResponseModel) => {
    const currentAllowedCount = editedActions[planAction.planId]?.size || 0;
    return {
      key: planAction.planId,
      label: (
        <span>
          {PLAN_TYPE_LABELS[planAction.planType] || planAction.planType}
          <Tag color="blue" style={{ marginLeft: 8 }}>
            {currentAllowedCount}개
          </Tag>
        </span>
      ),
      children: (
        <div style={{ maxWidth: 450 }}>
          <Table
            dataSource={getDataSource(planAction.planId)}
            columns={getColumns(planAction.planId)}
            pagination={false}
            size="small"
            rowKey="key"
          />
          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Button
              type="primary"
              onClick={() => openConfirmModal(planAction.planId)}
              loading={saving}
              disabled={!hasChanges(planAction.planId)}
            >
              저장
            </Button>
          </div>
        </div>
      ),
    };
  });

  return (
    <DefaultLayout>
      <PageHeader
        title="요금제 액션 관리"
        subTitle="요금제별 허용 액션을 관리합니다."
        style={{ margin: '16px 20px 0px 20px' }}
      />
      <Divider />
      <Content style={{ padding: '0 24px', margin: '0px 20px', marginBottom: '44px' }}>
        {planActionsState.loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        ) : planActionsState.error ? (
          <div style={{ textAlign: 'center', padding: '50px', color: 'red' }}>
            데이터를 불러오는 중 오류가 발생했습니다.
          </div>
        ) : (
          <Tabs
            items={tabItems}
            activeKey={activeTab}
            onChange={setActiveTab}
          />
        )}
        <Modal
          open={confirmModal.visible}
          onCancel={closeConfirmModal}
          footer={null}
          centered
          closable={false}
        >
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <p style={{ fontSize: 16, marginBottom: 24 }}>
              요금제 사용자들에게 즉시 적용됩니다.
              <br />
              정말 적용하시겠습니까?
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
              <Button onClick={closeConfirmModal}>
                아니요
              </Button>
              <Button
                type="primary"
                onClick={handleConfirmSave}
                disabled={countdown > 0}
                loading={saving}
              >
                {countdown > 0 ? `예 (${countdown})` : '예'}
              </Button>
            </div>
          </div>
        </Modal>
      </Content>
    </DefaultLayout>
  );
}
